import { produce } from 'immer';
import { create } from 'zustand';

import { getFeatureFlag, getFeatureFlagByEmail } from '~/services/featureFlags/FeatureFlagsService';

export type IFeatureFlag = {
  name: string;
  isEnabled: boolean;
};

export type IFeatureFlagState = {
  flags: IFeatureFlag[];
  isLoading: boolean;
  isError: boolean;
  isSuccessful: boolean;
};

export type IFeatureFlagActions = {
  fetchFeatureFlags: (userEmail?: string) => Promise<void>;
  refreshFeatureFlags: (userEmail?: string) => Promise<void>;
  refreshSingleFeatureFlag: (flagName: FeatureFlagsEnum) => Promise<void>;
  setState: (newState: Partial<IFeatureFlagState>) => void;
  isFeatureFlagEnabled: (flagName: FeatureFlagsEnum) => boolean;
  isFeatureFlagEnabledForUser: (flagName: FeatureFlagsEnum, email?: string) => boolean;
  isEmailFlagEnabled: (flagName: FeatureFlagsEnum, email?: string) => boolean;
};

export type IFeatureFlagStore = IFeatureFlagState & IFeatureFlagActions;

const initialFeatureFlagState: IFeatureFlagState = {
  flags: [],
  isLoading: false,
  isError: false,
  isSuccessful: false,
};

export enum FeatureFlagsEnum {
  RealPhotos = 'RealPhotos',
  Notifications = 'Notifications',
  WhatsAppNotification = 'WhatsAppNotification',
  EditProfile = 'EditProfile',
  Devices = 'Devices',
}

const predefinedFlags = Object.values(FeatureFlagsEnum);

const flagsWithEmailSupport: FeatureFlagsEnum[] = [
  FeatureFlagsEnum.RealPhotos,
  FeatureFlagsEnum.Notifications,
  FeatureFlagsEnum.EditProfile,
  FeatureFlagsEnum.Devices,
];

export const useFeatureFlagStore = create<IFeatureFlagStore>((set, get) => ({
  ...initialFeatureFlagState,

  setState: (newState: Partial<IFeatureFlagState>) => {
    set(
      produce((state: IFeatureFlagStore) => {
        Object.assign(state, newState);
      }),
    );
  },

  fetchFeatureFlags: async (userEmail?: string) => {
    const { setState } = get();

    setState({ isLoading: true });

    try {
      const generalFlags = await Promise.all(
        predefinedFlags.map(flag =>
          getFeatureFlag({ flag }).catch(() => ({
            name: flag,
            isEnabled: false,
          })),
        ),
      );

      let emailFlags: IFeatureFlag[] = [];
      if (userEmail) {
        emailFlags = await Promise.all(
          flagsWithEmailSupport.map(flag =>
            getFeatureFlagByEmail({ flag, email: userEmail }).catch(() => ({
              name: `${flag}:${userEmail}`,
              isEnabled: false,
            })),
          ),
        );
      }

      setState({
        flags: [...generalFlags, ...emailFlags],
        isError: false,
        isLoading: false,
        isSuccessful: true,
      });
    } catch (error) {
      console.error('[FeatureFlag] Erro ao carregar flags:', error);
      setState({
        flags: [],
        isError: true,
        isLoading: false,
        isSuccessful: false,
      });
    }
  },

  refreshFeatureFlags: async (userEmail?: string) => {
    const { fetchFeatureFlags } = get();
    await fetchFeatureFlags(userEmail);
  },

  refreshSingleFeatureFlag: async (flagName: FeatureFlagsEnum) => {
    const { setState, flags } = get();

    try {
      const updatedFlag = await getFeatureFlag({ flag: flagName });
      const updatedFlags = flags.map(flag => (flag.name === flagName ? updatedFlag : flag));

      setState({
        flags: updatedFlags,
        isError: false,
        isLoading: false,
        isSuccessful: true,
      });
    } catch (error) {
      console.error('[FeatureFlag] Erro ao atualizar flag:', error);
      setState({
        isError: true,
        isLoading: false,
        isSuccessful: false,
      });
    }
  },

  isFeatureFlagEnabled: (flagName: FeatureFlagsEnum): boolean => {
    const { flags } = get();
    const flag = flags.find(f => f.name === flagName);
    return flag?.isEnabled ?? false;
  },

  isFeatureFlagEnabledForUser: (flagName: FeatureFlagsEnum, email?: string): boolean => {
    const { flags } = get();

    const generalFlag = flags.find(f => f.name === flagName);
    const isGeneralEnabled = generalFlag?.isEnabled ?? false;

    if (email) {
      const emailFlagName = `${flagName}:${email}`;
      const emailFlag = flags.find(f => f.name === emailFlagName);
      const isEmailEnabled = emailFlag?.isEnabled ?? false;

      return isGeneralEnabled || isEmailEnabled;
    }

    return isGeneralEnabled;
  },

  isEmailFlagEnabled: (flagName: FeatureFlagsEnum, email?: string): boolean => {
    const { flags } = get();

    if (!email) return false;

    const emailFlagName = `${flagName}:${email}`;
    const emailFlag = flags.find(f => f.name === emailFlagName);
    return emailFlag?.isEnabled ?? false;
  },
}));
